/**
 * 题目详细信息展示组件
 * 
 * 功能:
 * 1. 根据题型自动判断展示策略（客观题/非客观题）
 * 2. 题干区域：使用 QuestionHtmlWithZoom 渲染，支持图片布局和位置
 * 3. 选项区域：仅客观题显示，网格布局，圆形编号，正确答案高亮
 * 4. 答案+解析区域：灰色背景卡片，显示参考答案和详细解析
 * 
 * 使用场景:
 * - CorrectionDetailModal（纠错详情弹窗）
 * - ReportErrorModal（纠错提交弹窗）
 * - QuizReviewMode（错题回顾模式）- 可选
 */

import { useRef, useEffect } from 'react';
import { CheckCircle, BookOpen } from 'lucide-react';
import QuizQuestionTitle from './admin/QuizQuestionTitle';
import { getSupabaseUrl } from '../supabase/client';
import { watchImageHeight } from '../utils/imageHeightLimit';

interface QuestionDetailDisplayProps {
  // 题目基本信息
  orderIndex?: number | null;           // 题号
  questionType?: string | null;         // 题型（single_choice/multi_choice/judge/python_coding/cpp_coding/scratch_coding等）
  title?: string | null;                // 题目名称（非客观题专用，如"未命名1"）
  content?: string | null;              // 题干内容（富文本HTML）

  // 客观题特有字段
  questionOptions?: any[] | null;       // 选项数组（仅客观题有）
  optionLayout?: string | null;         // 选项布局（grid/list，仅客观题）

  // 图片相关
  titleImages?: string[] | null;        // 题干图片URL数组
  titleImageWidths?: number[] | null;   // 题干图片宽度百分比
  titleImageLayout?: string | null;     // 题干图片布局（single/multi）
  imagePosition?: string | null;        // 图片位置（above/below）

  // 答案和解析（所有题型通用）
  referenceAnswer?: string | null;      // 参考答案（客观题：A/B/C/D，编程题：代码或描述）
  explanation?: string | null;          // 题目解析
  scoringPoints?: string | null;        // 评分规则（非客观题专用）

  // 显示控制
  showOptions?: boolean;                // 是否显示选项（默认true，编程题自动为false）
  showAnswer?: boolean;                 // 是否显示答案（默认false）
  showExplanation?: boolean;            // 是否显示解析（默认false）
  showScoringRules?: boolean;           // 是否显示评分规则（默认false，仅非客观题）

  // 样式定制
  className?: string;                   // 自定义类名
}

// 客观题类型列表
const OBJECTIVE_TYPES = ['single_choice', 'multi_choice', 'multiple_choice', 'judge'];

export default function QuestionDetailDisplay({
  orderIndex,
  questionType,
  title,
  content,
  questionOptions,
  optionLayout = 'grid',
  titleImages,
  titleImageWidths,
  titleImageLayout = 'multi',
  imagePosition = 'below',
  referenceAnswer,
  explanation,
  scoringPoints,
  showOptions = true,
  showAnswer = false,
  showExplanation = false,
  showScoringRules = false,
  className = '',
}: QuestionDetailDisplayProps) {
  // 选项图片容器 ref — 接入与富文本编辑器一致的图片高度限制
  const optionImagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = optionImagesContainerRef.current;
    if (!container) return;

    let cleanupFn: (() => void) | null = null;
    let cancelled = false;

    const rafId = requestAnimationFrame(() => {
      if (cancelled) return;
      try {
        cleanupFn = watchImageHeight(container);
      } catch (error) {
        console.warn('Failed to initialize detail display image height observer:', error);
      }
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
      if (cleanupFn) cleanupFn();
    };
  }, [questionOptions]);

  const displayTitle = title;
  const displayContent = content;
  // 判断是否为客观题
  const isObjective = questionType ? OBJECTIVE_TYPES.includes(questionType) : false;
  
  // 判断是否有题目数据
  const hasQuestionData = displayContent || (isObjective && questionOptions && questionOptions.length > 0);
  
  if (!hasQuestionData) {
    return null;
  }

  // 组装 question 对象传给 QuizQuestionTitle
  const questionAsObject: any = {
    id: `detail-${orderIndex ?? 0}`,
    type: questionType || 'single_choice',
    title: title || '',
    content: content || '',
    titleImages: titleImages || [],
    titleImageWidths: titleImageWidths || [],
    titleImageLayout: titleImageLayout || 'auto',
    imagePosition: imagePosition || 'below',
  };

  return (
    <div className={className}>
      {/* 题干区域 - 所有题型都显示 */}
      <div className="mb-6">
        <QuizQuestionTitle
          question={questionAsObject}
          currentIndex={orderIndex ? orderIndex - 1 : 0}
          examQuestions={[questionAsObject]}
          isPreview={false}
          sizeVariant='compact'
        />
      </div>

      {/* 选项区域 - 仅客观题显示 */}
      {isObjective && showOptions && questionOptions && questionOptions.length > 0 && (
        <div className="mb-6">
          <div ref={optionImagesContainerRef} className={`grid gap-3 ${optionLayout === 'grid' ? 'grid-cols-1 md:grid-cols-2' : 'grid-cols-1'}`}>
            {questionOptions.map((option: any, idx: number) => {
              const optId = option.id || String.fromCharCode(65 + idx);
              const optText = option.text || '';
              const isCorrect = option.isCorrect;

              return (
                <div
                  key={idx}
                  className={`w-full p-3 sm:p-4 rounded-lg border-2 text-left flex items-start gap-2 sm:gap-3 ${
                    isCorrect
                      ? 'border-green-500 bg-green-50 shadow-sm'
                      : 'border-gray-200 bg-white'
                  }`}
                >
                  <span className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 flex items-center justify-center text-xs sm:text-sm font-medium ${
                    isCorrect
                      ? 'border-green-500 bg-green-500 text-white'
                      : 'border-gray-300 text-gray-500 bg-white'
                  }`}>
                    {optId}
                  </span>
                  <div className="flex-1 text-gray-800">
                    {optText?.startsWith('<') ? (
                      <div dangerouslySetInnerHTML={{ __html: optText }} />
                    ) : (
                      <span>{optText}</span>
                    )}
                    {/* 处理选项图片(支持多张) */}
                    {(() => {
                      const optionImages = option.images && option.images.length > 0
                        ? option.images
                        : (option.image ? [option.image] : []);
                      const optionImageWidths = option.imageWidths && option.imageWidths.length > 0
                        ? option.imageWidths
                        : (option.imageWidth ? [option.imageWidth] : []);

                      if (optionImages.length === 0) return null;

                      return (
                        <div className="mt-2 grid grid-cols-2 gap-3">
                          {optionImages.map((imgUrl: string, imgIdx: number) => {
                            const displayWidth = optionImageWidths[imgIdx] ?? 100;
                            return (
                              <div key={imgIdx}>
                                <img
                                  src={imgUrl.startsWith('/sb-api/') ? imgUrl : imgUrl.startsWith('/storage/') ? `${getSupabaseUrl()}${imgUrl}` : imgUrl}
                                  alt={`选项${optId}图片${imgIdx + 1}`}
                                  className="rounded-lg border border-gray-200"
                                  style={{ width: `${displayWidth}%`, height: 'auto', objectFit: 'contain' }}
                                  loading="lazy"
                                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                />
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                  {isCorrect && <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 答案+解析区域 - 根据题型差异化显示 */}
      {(showAnswer || showExplanation) && (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
          <div className="space-y-3 text-sm">
            {/* 参考答案 - 仅非客观题显示（客观题选项已标识答案） */}
            {!isObjective && showAnswer && (
              <div className="flex items-start gap-2">
                <span className="text-gray-600 flex-shrink-0">参考答案：</span>
                <span className={referenceAnswer ? "font-medium text-green-600" : "text-gray-400 italic"}>
                  {referenceAnswer || "暂无参考答案"}
                </span>
              </div>
            )}

            {/* 答案解析 - 所有题型都显示 */}
            {showExplanation && (
              <div className={!isObjective && showAnswer ? "pt-3 border-t border-gray-200" : ""}>
                <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-blue-600" />答案解析
                </h4>
                <div className={explanation ? "text-gray-700 leading-relaxed whitespace-pre-wrap" : "text-gray-400 italic"}>
                  {explanation || "暂无解析"}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 评分规则区域 - 仅非客观题显示 */}
      {!isObjective && showScoringRules && (
        <div className="mt-4 bg-purple-50 rounded-lg border border-purple-100 p-4">
          <div className="text-sm">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-medium text-gray-700">评分规则</span>
            </div>
            <div className={scoringPoints ? "text-gray-800 whitespace-pre-wrap" : "text-gray-400 italic"}>
              {scoringPoints || "暂无评分规则"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
